import { create } from "zustand";
import {
  APPROVED_DRAWING_COLORS,
  SHARED_STROKE_WIDTH_DEFAULT,
  SHARED_STROKE_WIDTH_MAX,
  SHARED_STROKE_WIDTH_MIN,
  type SharedColor,
  type SharedShape,
} from "@/lib/canvas/shared-shape";
import { TELESTRATOR_TOOL_KINDS } from "@/lib/canvas/telestrator-schema";

/**
 * Editor state powering BOTH canvas surfaces.
 *
 * One store, two surfaces — the tactical board and the video
 * telestrator each get their own armed-tool/color/stroke selection,
 * AND each owns its own pending-shape during a drag. Storing them
 * separately means a coach can switch between surfaces in Coach
 * Review without losing the in-progress drag on the other surface.
 *
 * Tool kinds:
 *   - Shared (both surfaces): select, arrow, line, zone, label,
 *     freehand
 *   - Tactical-only: player, ball
 *   - Telestrator-only: spotlight, dim, formation
 *
 * The store's `armTool` action validates the tool against the
 * surface's allowed set; an invalid combination falls back to
 * "select" rather than throwing. That keeps the UI predictable
 * (clicking a tool always succeeds visually) without letting an
 * editor land in an unreachable state.
 */

export type EditorSurface = "tactical" | "telestrator";

/** Every tool kind across both surfaces. Type-level union; the
 * runtime validity is per-surface and checked at arm time. */
export type EditorToolKind =
  | "select"
  | "arrow"
  | "line"
  | "zone"
  | "circle"
  | "label"
  | "freehand"
  | "player"
  | "ball"
  | "spotlight"
  | "dim"
  | "formation";

/** Subset of tools the tactical-board surface allows. Matches
 * `VALID_SHAPE_KINDS` + tokens + `select`. */
const TACTICAL_TOOLS: ReadonlySet<EditorToolKind> = new Set<EditorToolKind>([
  "select",
  "arrow",
  "line",
  "zone",
  "label",
  "freehand",
  "player",
  "ball",
]);

/** Subset of tools the telestrator surface allows. Matches
 * `TELESTRATOR_TOOL_KINDS`. Built from the imported constant to
 * stay in sync with the schema module. */
const TELESTRATOR_TOOLS: ReadonlySet<EditorToolKind> = new Set<EditorToolKind>(
  TELESTRATOR_TOOL_KINDS as readonly EditorToolKind[],
);

/** Return true when the given tool is allowed on the given surface. */
export function isToolAllowed(
  tool: EditorToolKind,
  surface: EditorSurface,
): boolean {
  const allowed = surface === "tactical" ? TACTICAL_TOOLS : TELESTRATOR_TOOLS;
  return allowed.has(tool);
}

/** Per-surface mutable editor state. Each surface has its own copy
 * so switching back-and-forth preserves selection + the armed tool. */
type SurfaceEditorState = {
  armedTool: EditorToolKind;
  color: SharedColor;
  strokeWidth: number;
  selectedShapeId: string | null;
  pendingShape: SharedShape | null;
};

const initialSurfaceState = (): SurfaceEditorState => ({
  armedTool: "select",
  color: APPROVED_DRAWING_COLORS[0],
  strokeWidth: SHARED_STROKE_WIDTH_DEFAULT,
  selectedShapeId: null,
  pendingShape: null,
});

/** Full editor state. */
export type ReviewEditorState = {
  /** Which surface the editor is currently showing. */
  surface: EditorSurface;
  /** Per-surface state. Both surfaces always have an entry — they
   * preserve their state even when not active. */
  tactical: SurfaceEditorState;
  telestrator: SurfaceEditorState;

  // ---- Surface ----
  setSurface(surface: EditorSurface): void;

  // ---- Tool / palette ----
  /** Arm a tool on the CURRENT surface. If the tool isn't allowed on
   * this surface, falls back to "select" (silent — matches the
   * legacy painter's "armed tool is sticky until something tells it
   * to change" behavior). */
  armTool(tool: EditorToolKind): void;
  /** Reset to select on the CURRENT surface AND clear selection. */
  disarm(): void;
  setColor(color: SharedColor): void;
  setStrokeWidth(width: number): void;

  // ---- Selection ----
  selectShape(id: string | null): void;

  // ---- In-drag pending shape ----
  /** Set the current drag's draft shape (clobbers any previous
   * pending — re-arming a tool while mid-drag is allowed). */
  startPending(shape: SharedShape): void;
  /** Update the drag's current shape. Idempotent — call repeatedly
   * from mouse-move handlers. */
  updatePending(shape: SharedShape): void;
  /** Returns the pending shape and clears it. Caller is responsible
   * for committing the returned shape to the scene. Returns null if
   * there's no pending shape. */
  commitPending(): SharedShape | null;
  /** Drop the pending shape (e.g. on Esc or mouse-leave outside a
   * commit window). */
  cancelPending(): void;

  // ---- Bulk reset (used when loading a new scene) ----
  resetSurface(surface: EditorSurface): void;
};

export const useReviewEditorStore = create<ReviewEditorState>((set, get) => ({
  surface: "tactical",
  tactical: initialSurfaceState(),
  telestrator: initialSurfaceState(),

  setSurface: (surface) => set({ surface }),

  armTool: (tool) =>
    set((state) => {
      const surface = state.surface;
      const next = isToolAllowed(tool, surface) ? tool : "select";
      const current = state[surface];
      return {
        [surface]: { ...current, armedTool: next, pendingShape: null },
      } as Partial<ReviewEditorState>;
    }),

  disarm: () =>
    set((state) => {
      const surface = state.surface;
      const current = state[surface];
      return {
        [surface]: {
          ...current,
          armedTool: "select" as EditorToolKind,
          selectedShapeId: null,
          pendingShape: null,
        },
      } as Partial<ReviewEditorState>;
    }),

  setColor: (color) =>
    set((state) => {
      const surface = state.surface;
      const current = state[surface];
      return {
        [surface]: { ...current, color },
      } as Partial<ReviewEditorState>;
    }),

  setStrokeWidth: (width) =>
    set((state) => {
      const clamped = Math.max(
        SHARED_STROKE_WIDTH_MIN,
        Math.min(SHARED_STROKE_WIDTH_MAX, Math.round(width)),
      );
      const surface = state.surface;
      const current = state[surface];
      return {
        [surface]: { ...current, strokeWidth: clamped },
      } as Partial<ReviewEditorState>;
    }),

  selectShape: (id) =>
    set((state) => {
      const surface = state.surface;
      const current = state[surface];
      return {
        [surface]: { ...current, selectedShapeId: id },
      } as Partial<ReviewEditorState>;
    }),

  startPending: (shape) =>
    set((state) => {
      const surface = state.surface;
      const current = state[surface];
      return {
        [surface]: { ...current, pendingShape: shape },
      } as Partial<ReviewEditorState>;
    }),

  updatePending: (shape) =>
    set((state) => {
      const surface = state.surface;
      const current = state[surface];
      // If there's no pending shape, treat the update as a start —
      // the drag handler doesn't have to coordinate which call fires
      // first.
      return {
        [surface]: { ...current, pendingShape: shape },
      } as Partial<ReviewEditorState>;
    }),

  commitPending: () => {
    const state = get();
    const surface = state.surface;
    const current = state[surface];
    const shape = current.pendingShape;
    if (shape === null) return null;
    set({
      [surface]: { ...current, pendingShape: null },
    } as Partial<ReviewEditorState>);
    return shape;
  },

  cancelPending: () =>
    set((state) => {
      const surface = state.surface;
      const current = state[surface];
      return {
        [surface]: { ...current, pendingShape: null },
      } as Partial<ReviewEditorState>;
    }),

  resetSurface: (surface) =>
    set({
      [surface]: initialSurfaceState(),
    } as Partial<ReviewEditorState>),
}));

/** Non-hook accessor — current surface's snapshot. Useful inside
 * Konva event handlers that fire outside React render. */
export function currentSurfaceState(): SurfaceEditorState {
  const s = useReviewEditorStore.getState();
  return s[s.surface];
}

/** Selector hook for the current surface's state. Re-renders only
 * when something on THIS surface changes. Use this in components
 * that don't care which surface is active, just "what's the editor
 * doing right now". */
export function useCurrentSurfaceEditor(): SurfaceEditorState {
  return useReviewEditorStore((s) => s[s.surface]);
}
